import { defineMessages } from 'react-intl';

import { RadioGroup } from '#src/atoms/inputs/RadioGroup/index.tsx';
import { CreditCard } from '#src/components/buyActions/CreditCard/index.tsx';
import { CryptoWallet } from '#src/components/paymentMethods/CryptoWallet/index.tsx';
import { FiatWallet } from '#src/components/paymentMethods/FiatWallet/index.tsx';
import { useCurrentUserPaymentMethods } from '#src/components/settings/PaymentMethods/useCurrentUserPaymentMethods.ts';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { usePreferredPaymentMethod } from '#src/hooks/wallets/usePreferredPaymentMethod.ts';
import type { PaymentProvider_paymentMethod } from '#src/lib/paymentMethod.ts';
import { PaymentMethod } from '#src/lib/paymentMethod.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Choose your preferred payment method',
  },
});

export const PreferredPaymentMethods = () => {
  const {
    walletPreferences: { showCryptoWallet, showFiatWallet },
  } = useCurrentUserContext();
  const creditCards = useCurrentUserPaymentMethods('cache-only');
  const { preferredOrderedPaymentMethod, updatePreferredPaymentMethod } =
    usePreferredPaymentMethod();

  const paymentMethods: Record<
    string,
    | PaymentProvider_paymentMethod
    | PaymentMethod.CRYPTO_WALLET
    | PaymentMethod.FIAT_WALLET
  > = {
    [PaymentMethod.CRYPTO_WALLET]: PaymentMethod.CRYPTO_WALLET,
    [PaymentMethod.FIAT_WALLET]: PaymentMethod.FIAT_WALLET,
    ...creditCards?.reduce<Record<string, PaymentProvider_paymentMethod>>(
      (acc, cc) => ({
        ...acc,
        [`credit_card_${cc.id}`]: cc,
      }),
      {}
    ),
  };

  const options = [
    ...(showCryptoWallet
      ? [
          {
            label: <CryptoWallet withoutBalance />,
            value: PaymentMethod.CRYPTO_WALLET,
          },
        ]
      : []),
    ...(showFiatWallet
      ? [
          {
            label: <FiatWallet withoutBalance />,
            value: PaymentMethod.FIAT_WALLET,
          },
        ]
      : []),
    ...creditCards
      .filter(pm => !pm.disabled)
      .map(creditCard => ({
        label: (
          <CreditCard
            creditCard={creditCard.card}
            color={creditCard.disabled ? 'var(--c-nd-600)' : undefined}
          />
        ),
        value: `credit_card_${creditCard.id}`,
      })),
  ];

  return (
    <SettingsSection title={messages.title}>
      <RadioGroup
        preventPreselection
        value={preferredOrderedPaymentMethod || undefined}
        withSpacing
        name="preferred-currency"
        options={options}
        onChange={(e: string) => {
          updatePreferredPaymentMethod(paymentMethods[e]);
        }}
      />
    </SettingsSection>
  );
};
